/**
 * The discipline slice of the store (state.ext.discipline, live over SSE), the rows the
 * page shows, and "check now". In demo mode the slice is filled from mock.ts and a
 * check is simulated, so the page behaves the same on ?mock=1.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { DisciplineReport, DisciplineState, Id } from '@orgus/protocol';
import { apiPost } from '../../lib/api.ts';
import { useOrgus, useProjects } from '../../lib/store.ts';
import { mockDisciplineExtras, mockDisciplineState, scoreFor, summarize } from './mock.ts';
import { fleetSummary, repoRows } from './model.ts';

export function useDisciplineState(): DisciplineState | undefined {
  return useOrgus((s) => s.ext.discipline as DisciplineState | undefined);
}

export function useDiscipline() {
  const state = useDisciplineState();
  const projects = useProjects();
  const { machines, mode, phase, sessions } = useOrgus(useShallow((s) => ({ machines: s.machines, mode: s.mode, phase: s.phase, sessions: s.sessions })));

  useEffect(() => {
    if (mode !== 'mock' || phase !== 'ready' || state) return;
    const now = Date.now();
    const data = mockDisciplineExtras(mockDisciplineState(projects, Object.values(machines), now), projects, Object.values(sessions), now);
    useOrgus.getState().applyExt({ ext: 'discipline', type: 'state', data });
  }, [mode, phase, state, projects, machines, sessions]);

  const rows = useMemo(() => repoRows(projects, state, machines), [projects, state, machines]);
  const summary = useMemo(() => fleetSummary(rows), [rows]);

  const checkNow = useCallback(
    async (projectId?: Id): Promise<void> => {
      if (mode === 'mock') {
        await new Promise((r) => setTimeout(r, 900));
        const cur = useOrgus.getState().ext.discipline as DisciplineState | undefined;
        if (!cur) return;
        const now = Date.now();
        const reports = cur.reports.map((r): DisciplineReport => {
          if (projectId && r.projectId !== projectId) return r;
          if (r.error) return { ...r, checkedAt: now };
          const violations = r.violations.filter((_, i) => i !== 0 || r.violations.length < 2);
          const rules = summarize(r.pack ?? 'recommended', violations, r.waived);
          return { ...r, violations, rules, score: scoreFor(rules), checkedAt: now };
        });
        const history = { ...cur.history };
        for (const r of reports) {
          if (r.error || (projectId && r.projectId !== projectId)) continue;
          history[r.projectId] = [...(history[r.projectId] ?? []), { at: now, score: r.score, errors: 0, warns: 0 }].slice(-60);
        }
        useOrgus.getState().applyExt({ ext: 'discipline', type: 'state', data: { ...cur, reports, history } });
        return;
      }
      await apiPost<{ sent: number }>('/api/ext/discipline/check', projectId ? { projectId } : {});
    },
    [mode],
  );

  return { state, rows, summary, checkNow, mode };
}
